/**
 * Cacheability rules and key construction. Pure — no I/O, no storage.
 */

export type CacheKind = 'chat' | 'hrms';

/**
 * Endpoints that must never be written to disk. Money and identity data stays
 * live-only so a shared machine never holds it, and short-lived signed URLs
 * would be useless (or misleading) once replayed from cache.
 */
const NEVER_CACHE: RegExp[] = [
  /\/api\/auth\//i,
  /payroll/i,
  /salary/i,
  /tax-declaration/i,
  /face-check/i,
  /\/kiosk/i,
  /\/attachments\/[^/]+\/download/i,
  /signed-url/i,
  // Live-only status endpoints. They are re-fetched on a timer or on every
  // reconnect, so a disk copy is never what the screen should show — and
  // writing each poll response to IndexedDB (JSON.stringify + a put, plus a
  // full-store size scan every 50 writes) was steady background CPU for data
  // that is stale seconds later.
  //
  // `/attendanceAll` deliberately stays cacheable: it is a per-day record set,
  // not a live status poll, and the sidebar clock reads it through the
  // serve-stale path to keep showing a running punch while the network or the
  // backend is down. Its write volume is no longer a concern now that the
  // presence reconcile runs every 5 minutes and only while visible.
  /\/presence\/snapshot/i,
  /\/away\/me/i,
  /\/session\/heartbeat/i,
];

export function isCacheableRequest(
  method: string | undefined,
  url: string | undefined,
): boolean {
  if (!url) return false;
  if ((method ?? 'get').toLowerCase() !== 'get') return false;
  return !NEVER_CACHE.some((re) => re.test(url));
}

export function buildCacheKey(input: {
  ns: string;
  kind: CacheKind;
  url: string;
  params?: Record<string, unknown>;
}): string {
  const { ns, kind, url, params } = input;
  const query = Object.entries(params ?? {})
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => [k, String(v)] as const)
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${ns}|${kind}|GET ${url}${query ? `?${query}` : ''}`;
}
